import React, { useState, lazy } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { MessageCircle, Calendar, ArrowRight, MapPin, Microscope, Scan, MonitorPlay, ShieldCheck, Languages, CreditCard, Phone, Star, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { CallbackForm } from '../components/CallbackForm';
import { CallbackModal } from '../components/CallbackModal';
import { GoogleReviews } from '../components/GoogleReviews';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const MAPS_URL = 'https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function FaqAccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1610">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1611">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-1612">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-1613">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1614" /> : <Plus className="w-4 h-4" data-id="element-1615" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1616">
        <p className="text-gray-600 leading-relaxed" data-id="element-1617">{answer}</p>
      </div>
    </div>;
}
export function HomePage() {
  const {
    t,
    lang
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Чи є український стоматолог у Лондоні?',
    a: 'Так, Dentico Clinic — приватна стоматологічна клініка в Angel, Central London (EC1V 2PY), де лікарі консультують українською мовою.'
  }, {
    q: 'Де знайти українську стоматологію в центрі Лондона?',
    a: 'Dentico Clinic розташована за адресою 360-364 City Road, London EC1V 2PY — 5 хвилин від станції Angel. Ми пропонуємо повний спектр стоматологічних послуг українською мовою.'
  }, {
    q: 'Чи можна записатися онлайн?',
    a: 'Так, ви можете записатися онлайн через наш сайт, WhatsApp або Telegram. Запис доступний 24/7.'
  }, {
    q: 'Чи приймає клініка біля Angel Station?',
    a: 'Так, Dentico Clinic знаходиться в 5 хвилинах ходьби від Angel Station (Northern Line). Адреса: 360-364 City Road, EC1V 2PY.'
  }];
  const services = [{
    id: 'fillings',
    title: t('services.fillings.title'),
    desc: t('services.fillings.desc'),
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'root-canal',
    title: t('services.rootCanal.title'),
    desc: t('services.rootCanal.desc'),
    image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'crowns',
    title: t('services.crowns.title'),
    desc: t('services.crowns.desc'),
    image: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'hygiene',
    title: t('services.hygiene.title'),
    desc: t('services.hygiene.desc'),
    image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'whitening',
    title: t('services.whitening.title'),
    desc: t('services.whitening.desc'),
    image: 'https://images.unsplash.com/photo-1606265752439-1f18756aa5fc?auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'smile-makeover',
    title: t('services.smileMakeover.title'),
    desc: t('services.smileMakeover.desc'),
    image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=800&q=80'
  }];
  const advantages = [{
    icon: Microscope,
    title: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Microscop Dentar' : 'Dental Microscope' : lang === 'ua' ? 'Стоматологічний мікроскоп' : 'Стоматологический микроскоп',
    desc: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Precizie mai mare pentru tratamentele de canal' : 'Higher precision for root canals' : lang === 'ua' ? 'Вища точність лікування каналів' : 'Высокая точность лечения каналов'
  }, {
    icon: Scan,
    title: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Scanner Digital 3D' : '3D Digital Scanner' : lang === 'ua' ? 'Цифровий 3D сканер' : 'Цифровой 3D сканер',
    desc: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Fără amprente dezordonate' : 'No messy impressions needed' : lang === 'ua' ? 'Без відбитків — миттєвий результат' : 'Без слепков — мгновенный результат'
  }, {
    icon: MonitorPlay,
    title: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Design Digital al Zâmbetului' : 'Digital Smile Design' : lang === 'ua' ? 'Цифровий дизайн посмішки' : 'Цифровой дизайн улыбки',
    desc: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Vedeți rezultatul înainte de tratament' : 'See your result before treatment' : lang === 'ua' ? 'Побачте результат до лікування' : 'Увидьте результат до лечения'
  }, {
    icon: ShieldCheck,
    title: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Standarde de Sterilizare UK' : 'UK Sterilisation Standards' : lang === 'ua' ? 'Стандарти стерилізації UK' : 'Стандарты стерилизации UK',
    desc: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'HTM 01-05 — siguranță de încredere' : 'HTM 01-05 — safety you can trust' : lang === 'ua' ? 'HTM 01-05 — безпека, якій довіряють' : 'HTM 01-05 — безопасность, которой доверяют'
  }, {
    icon: Languages,
    title: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Echipă Multilingvă' : 'Multilingual Team' : lang === 'ua' ? 'Багатомовна команда' : 'Многоязычная команда',
    desc: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Engleză · Ucraineană · Rusă' : 'English · Ukrainian · Russian' : lang === 'ua' ? 'Українська · English · Російська' : 'Русский · English · Українська'
  }, {
    icon: CreditCard,
    title: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Prețuri Transparente' : 'Transparent Pricing' : lang === 'ua' ? 'Прозорі ціни' : 'Прозрачные цены',
    desc: lang === 'en' || lang === 'ro' ? lang === 'ro' ? 'Fără taxe ascunse — niciodată' : 'No hidden fees — ever' : lang === 'ua' ? 'Жодних прихованих платежів' : 'Никаких скрытых платежей'
  }];
  const badgeColors = {
    en: {
      bg: 'bg-teal-100',
      text: 'text-teal-800',
      dot: 'bg-teal-500'
    },
    ua: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border border-blue-100',
      dot: 'bg-blue-500'
    },
    ru: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-700',
      border: 'border border-indigo-100',
      dot: 'bg-indigo-500'
    },
    ro: {
      bg: 'bg-teal-100',
      text: 'text-teal-800',
      dot: 'bg-teal-500'
    }
  };
  const colors = badgeColors[lang];
  return <Layout data-id="element-1618">
      {/* Callback Modal */}
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1619" />

      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden" data-id="element-1620">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1621" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 lg:pt-16 lg:pb-20 relative" data-id="element-1622">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-1623">
            <div className="space-y-5 lg:space-y-6 order-2 lg:order-1" data-id="element-1624">
              {lang !== 'ro' && <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${colors.bg} ${colors.text} ${colors.border || ''} text-sm font-medium`} data-id="element-1625">
                  <span className="relative flex h-2 w-2" data-id="element-1626">
                    {lang === 'en' && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-1627"></span>}
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${colors.dot}`} data-id="element-1628"></span>
                  </span>
                  {t('hero.badge')}
                </div>}

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1629">
                {t('hero.title')}{' '}
                <span className="text-teal-600" data-id="element-1630">
                  {t('hero.titleHighlight')}
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-md" data-id="element-1631">
                {lang === 'en' ? 'Modern private dentistry with advanced technology and transparent fees.' : lang === 'ua' ? 'Сучасна приватна стоматологія з передовими технологіями та чесними цінами.' : lang === 'ro' ? 'Stomatologie privată modernă cu tehnologie avansată și tarife transparente.' : 'Современная частная стоматология с передовыми технологиями и честными ценами.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-3" data-id="element-1632">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1" data-id="element-1633">
                  <Button size="lg" fullWidth className="gap-2 shadow-lg shadow-teal-600/20" data-id="element-1634">
                    <Calendar className="w-5 h-5 flex-shrink-0" data-id="element-1635" />
                    {t('nav.bookOnline')}
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1" data-id="element-1636">
                  <Button size="lg" fullWidth className="gap-2 shadow-lg shadow-teal-600/20" data-id="element-1637">
                    <MessageCircle className="w-5 h-5 flex-shrink-0" data-id="element-1638" />
                    WhatsApp
                  </Button>
                </a>
                <div className="w-full sm:flex-1" data-id="element-1639">
                  <Button size="lg" fullWidth className="gap-2 shadow-lg shadow-teal-600/20" onClick={() => setIsCallbackOpen(true)} data-id="element-1640">
                    <Phone className="w-5 h-5 flex-shrink-0" data-id="element-1641" />
                    {lang === 'en' ? 'Request a Call' : lang === 'ua' ? 'Замовити дзвінок' : lang === 'ro' ? 'Solicitați un Apel' : 'Заказать звонок'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="relative order-1 lg:order-2" data-id="element-1642">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1643"></div>
              <video autoPlay muted loop playsInline preload="auto" poster="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=1200&q=80" className="relative rounded-3xl shadow-2xl object-cover h-[280px] sm:h-[360px] lg:h-[540px] w-full" data-id="element-1644">
                <source src="https://cdn.magicpatterns.com/uploads/eqA8aSQNT9wzTK1A4UWpDM/0307%20(1).mp4" type="video/mp4" data-id="element-1645" />
                {/* Fallback image if video cannot load */}
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=1200&q=80" alt="Beautiful smiling woman at dental clinic" className="rounded-3xl shadow-2xl object-cover h-[280px] sm:h-[360px] lg:h-[540px] w-full" data-id="element-1646" />
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals & Booking CTA Banner */}
      <section className="bg-teal-50 border-y border-teal-100 py-6 cursor-pointer transition-colors duration-200 hover:bg-teal-100/60" onClick={() => setIsCallbackOpen(true)} role="button" tabIndex={0} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsCallbackOpen(true);
      }
    }} data-id="element-1647">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1648">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-4" data-id="element-1649">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1650">
              <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-1651" />
              5★ Google
            </div>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1652">
              <MapPin className="w-4 h-4 text-teal-600" data-id="element-1653" />
              Central London, Angel
            </div>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1654">
              <Scan className="w-4 h-4 text-teal-600" data-id="element-1655" />
              {lang === 'en' ? 'Digital Dentistry' : lang === 'ua' ? 'Цифрова стоматологія' : lang === 'ro' ? 'Stomatologie Digitală' : 'Цифровая стоматология'}
            </div>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1656">
              <Languages className="w-4 h-4 text-teal-600" data-id="element-1657" />
              {lang === 'en' ? 'Multilingual Team' : lang === 'ua' ? 'Українська мова' : lang === 'ro' ? 'Echipă Multilingvă' : 'Русский язык'}
            </div>
          </div>
          <div className="text-center" data-id="element-1658">
            <button onClick={e => {
            e.stopPropagation();
            setIsCallbackOpen(true);
          }} className="text-sm font-semibold text-teal-700 hover:text-teal-900 hover:underline inline-flex items-center gap-1 transition-colors duration-150" data-id="element-1659">
              {lang === 'en' ? 'Looking for your dentist in London?' : lang === 'ua' ? 'Шукаєте українського стоматолога в Лондоні?' : lang === 'ro' ? 'Căutați un dentist vorbitor de română în Londra?' : 'Ищете русскоговорящего стоматолога в Лондоне?'}{' '}
              <ArrowRight className="w-3 h-3" data-id="element-1660" />
            </button>
          </div>
        </div>
      </section>

      {/* Visual Services Grid */}
      <section className="py-16 lg:py-20 bg-gray-50" data-id="element-1661">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1662">
          <div className="text-center max-w-xl mx-auto mb-12" data-id="element-1663">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-1664">
              {t('services.title')}
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-1665">
            {services.map((service, idx) => <Link key={idx} to={`/${lang}/services/${service.id}`} className="group" data-id="element-1666">
                <Card className="overflow-hidden flex flex-col h-full border-0 shadow-md" data-id="element-1667">
                  <div className="relative aspect-[4/3] overflow-hidden" data-id="element-1668">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1669" />
                  </div>
                  <div className="p-4 lg:p-5" data-id="element-1670">
                    <h3 className="text-base lg:text-lg font-bold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors" data-id="element-1671">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-xs lg:text-sm line-clamp-1" data-id="element-1672">
                      {service.desc}
                    </p>
                  </div>
                </Card>
              </Link>)}
          </div>

          <div className="mt-10 text-center" data-id="element-1673">
            <Link to={`/${lang}/services`} data-id="element-1674">
              <Button variant="secondary" size="lg" data-id="element-1675">
                {t('services.viewAll')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1676">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1677">
          <div className="text-center mb-12" data-id="element-1678">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-1679">
              {lang === 'en' ? 'Why Choose Dentico Clinic' : lang === 'ua' ? 'Чому обирають Dentico Clinic' : lang === 'ro' ? 'De Ce Să Alegeți Dentico Clinic' : 'Почему выбирают Dentico Clinic'}
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-1680">
            {advantages.map((adv, idx) => <div key={idx} className="flex flex-col items-center text-center p-5 lg:p-6 bg-gray-50 rounded-2xl" data-id="element-1681">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-3" data-id="element-1682">
                  <adv.icon className="w-6 h-6" data-id="element-1683" />
                </div>
                <h3 className="text-sm lg:text-base font-bold text-gray-900 mb-1" data-id="element-1684">
                  {adv.title}
                </h3>
                <p className="text-gray-500 text-xs lg:text-sm" data-id="element-1685">{adv.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 lg:py-20 bg-gray-50" data-id="element-1686">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1687">
          <div className="bg-teal-900 rounded-3xl overflow-hidden shadow-2xl" data-id="element-1688">
            <div className="grid md:grid-cols-2" data-id="element-1689">
              <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center" data-id="element-1690">
                <div className="flex items-center gap-2 text-teal-300 mb-4" data-id="element-1691">
                  <MapPin className="w-5 h-5" data-id="element-1692" />
                  <span className="font-medium tracking-wide uppercase text-sm" data-id="element-1693">
                    Central London
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1694">
                  Angel, Islington
                </h2>
                <p className="text-teal-100 mb-1 text-lg" data-id="element-1695">360–364 City Road</p>
                <p className="text-teal-100 mb-8 text-lg" data-id="element-1696">London, EC1V 2PY</p>
                <div className="flex flex-col sm:flex-row gap-3" data-id="element-1697">
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-id="element-1698">
                    <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-1699">
                      <MapPin className="w-4 h-4" data-id="element-1700" />
                      {t('location.getDirections')}
                    </Button>
                  </a>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-1701">
                    <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-1702">
                      <Calendar className="w-4 h-4" data-id="element-1703" />
                      {t('nav.bookOnline')}
                    </Button>
                  </a>
                </div>
              </div>
              <div className="h-64 md:h-auto bg-teal-800 relative min-h-[300px]" data-id="element-1704">
                <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" className="absolute inset-0" data-id="element-1705"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Callback Form */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1706">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1707">
          <CallbackForm data-id="element-1708" />
        </div>
      </section>

      {/* Reviews */}
      <GoogleReviews data-id="element-1709" />

      {/* UA ONLY: FAQ Section */}
      {lang === 'ua' && <section className="py-16 bg-white border-t border-gray-100" data-id="element-1710">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1711">
            <div className="text-center mb-10" data-id="element-1712">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1713">
                Часті запити про українського стоматолога в Лондоні
              </h2>
            </div>
            <div className="space-y-2" data-id="element-1714">
              {faqs.map((faq, idx) => <FaqAccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1715" />)}
            </div>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="py-16 bg-teal-900" data-id="element-1716">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-1717">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-6" data-id="element-1718">
            {lang === 'en' ? 'Ready to book your visit?' : lang === 'ua' ? 'Готові записатись?' : lang === 'ro' ? 'Sunteți gata să vă programați?' : 'Готовы записаться?'}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-1719">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1 sm:max-w-[220px]" data-id="element-1720">
              <Button size="lg" fullWidth className="gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-1721">
                <Calendar className="w-5 h-5 flex-shrink-0" data-id="element-1722" />
                {t('nav.bookOnline')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:flex-1 sm:max-w-[220px]" data-id="element-1723">
              <Button variant="outline" size="lg" fullWidth className="gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-1724">
                <MessageCircle className="w-5 h-5 flex-shrink-0" data-id="element-1725" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:flex-1 sm:max-w-[220px]" data-id="element-1726">
              <Button variant="outline" size="lg" fullWidth className="gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" onClick={() => setIsCallbackOpen(true)} data-id="element-1727">
                <Phone className="w-5 h-5 flex-shrink-0" data-id="element-1728" />
                {lang === 'en' ? 'Request a Call' : lang === 'ua' ? 'Замовити дзвінок' : lang === 'ro' ? 'Solicitați un Apel' : 'Заказать звонок'}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}